import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@dc-shared-web/lib'

const buttonVariants = cva(
  'tw-inline-flex tw-items-center tw-justify-center tw-rounded-md tw-text-sm tw-font-medium tw-ring-offset-background tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-primary-500 focus-visible:tw-ring-offset-2 disabled:tw-pointer-events-none disabled:tw-opacity-50',
  {
    variants: {
      variant: {
        default:
          'tw-bg-primary tw-text-primary-foreground hover:tw-bg-blue-500 tw-font-extralight',
        destructive:
          'tw-bg-destructive tw-text-destructive-foreground hover:tw-bg-destructive/90',
        outline:
          'tw-border tw-border-input tw-bg-background hover:tw-bg-accent hover:tw-text-accent-foreground',
        secondary:
          'tw-bg-secondary tw-text-secondary-foreground hover:tw-bg-secondary/80',
        gray: 'tw-bg-neutral-100 tw-text-neutral-400 hover:tw-bg-neutral-100',
        ghost: 'hover:tw-bg-accent hover:tw-text-accent-foreground',
        link: 'tw-text-primary tw-underline-offset-4 hover:tw-underline',
        icon: 'tw-bg-transparent',
        primitive: '',

        // Importing a dc-shared-web component into another project (Nexus)
        // and then trying to change the button color to something that doesn't exist during the build
        // causes colors to not change. As a temporary solution, we have to be explicit about the colors
        // i.e. primary-50, primary-100, etc. If we get to a point where we're not dependent on the
        // 'dc-shared-web' class specificity, then we won't need this approach.
        'primary-50':
          'tw-bg-primary-50 tw-text-primary-600 hover:tw-bg-primary-200',
        'destructive-600':
          'tw-bg-destructive-600 tw-text-destructive-foreground hover:tw-bg-destructive/90',
      },
      size: {
        default: 'tw-h-10 tw-px-3 tw-py-2',
        mini: 'tw-h-7 tw-rounded-md tw-px-2 tw-text-2xs',
        sm: 'tw-h-9 tw-rounded-md tw-px-3',
        lg: 'tw-h-11 tw-rounded-md tw-px-8',
        icon: 'tw-h-10 tw-w-10 tw-px-4 tw-py-2',
        xl: '',
      },
      fullWidth: {
        true: 'tw-w-full',
        false: '',
      },
      shape: {
        default: '',
        circle: 'tw-rounded-full',
        square: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      fullWidth: false,
      shape: 'default',
    },
  }
)

const newButtonVariants = cva(
  'tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-whitespace-nowrap tw-rounded-md tw-text-sm tw-font-medium tw-ring-offset-background tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-primary-500 focus-visible:tw-ring-offset-2 disabled:tw-text-neutral-400 [&_svg]:disabled:tw-text-neutral-400 disabled:tw-pointer-events-none [&_svg]:tw-pointer-events-none [&_svg]:tw-size-4 [&_svg]:tw-shrink-0',
  {
    variants: {
      variant: {
        default:
          'tw-bg-primary tw-text-primary-foreground [&_svg]:tw-text-primary-foreground hover:tw-bg-primary-700 focus:tw-bg-primary-800 active:tw-bg-primary-800 disabled:tw-bg-neutral-100',
        destructive:
          'tw-bg-destructive-600 tw-text-white [&_svg]:tw-text-white tw-shadow-sm hover:tw-bg-destructive-700 active:tw-bg-destructive-800 disabled:tw-bg-neutral-50',
        outline:
          'tw-bg-white tw-text-primary-950 tw-border tw-border-neutral-200 tw-shadow-sm tw-text-primary-950 [&_svg]:tw-text-primary-900 hover:tw-bg-primary-100 hover:tw-border-neutral-300 active:tw-bg-primary-200 disabled:tw-shadow-none',
        ghost:
          'tw-text-primary-600 [&_svg]:tw-text-primary-600 hover:tw-bg-primary-100 active:tw-bg-primary-200',
        /* Custom variants */
        primitive: '',
        /* These are other custom variants we're keeping to avoid breaking changes in the Button API but we should delete them once removing the env var. */
        'destructive-600': '',
        gray: '',
        icon: '',
        link: '',
        'primary-50': '',
        secondary: '',
      },
      size: {
        sm: 'tw-h-9 tw-px-3 tw-py-2 tw-text-2xs [&_svg]:tw-size-3',
        default: 'tw-h-10 tw-px-4 tw-py-2 [&_svg]:tw-size-3',
        lg: 'tw-h-11 tw-px-4 tw-py-2 tw-text-base [&_svg]:tw-size-4',
        xl: 'tw-h-12 tw-px-4 tw-py-2 tw-text-lg [&_svg]:tw-size-4',
        /* Custom sizes we won't use. */
        mini: '',
        icon: '',
      },
      fullWidth: {
        true: 'tw-w-full',
        false: '',
      },
      shape: {
        default: '',
        circle: 'tw-rounded-full',
        square: '',
      },
    },
    compoundVariants: [
      /* When the button is circled, we make the width the same as the height. */
      {
        size: 'sm',
        shape: 'circle',
        className: 'tw-w-9',
      },
      {
        size: 'sm',
        shape: 'square',
        className: 'tw-w-9 tw-p-0',
      },
      {
        size: 'default',
        shape: 'circle',
        className: 'tw-w-10',
      },
      {
        size: 'default',
        shape: 'square',
        className: 'tw-w-10 tw-p-0',
      },
      {
        size: 'lg',
        shape: 'circle',
        className: 'tw-w-11',
      },
      {
        size: 'lg',
        shape: 'square',
        className: 'tw-w-11 tw-p-0',
      },
      {
        size: 'xl',
        shape: 'circle',
        className: 'tw-w-12',
      },
      {
        size: 'xl',
        shape: 'square',
        className: 'tw-w-12 tw-p-0',
      },

      /* When the button is a ghost icon (circle or square), we make the icon darker. */
      {
        variant: 'ghost',
        shape: 'circle',
        className: '[&_svg]:tw-text-primary-900',
      },
      {
        variant: 'ghost',
        shape: 'square',
        className: '[&_svg]:tw-text-primary-900',
      },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'default',
      fullWidth: false,
      shape: 'default',
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, fullWidth, shape, asChild = false, ...props },
    ref
  ) => {
    let buttonSize = size
    let buttonVariant = variant
    let buttonVariantsFn = buttonVariants

    if (
      typeof window !== 'undefined' &&
      window.__VSS_UPDATES__?.buttonEnabled
    ) {
      if (variant === 'gray' || variant === 'link') {
        buttonVariant = 'ghost'
      } else if (variant === 'primary-50') {
        buttonVariant = 'default'
      } else if (variant === 'destructive-600') {
        buttonVariant = 'destructive'
      } else if (variant === 'icon') {
        buttonVariant = 'primitive'
      } else if (variant === 'secondary') {
        buttonVariant = 'outline'
      }

      if (size === 'mini') {
        buttonSize = 'sm'
      }

      buttonVariantsFn = newButtonVariants
    }

    const Comp = asChild ? Slot : 'button'

    return (
      <Comp
        className={cn(
          buttonVariantsFn({
            variant: buttonVariant,
            size: buttonSize,
            fullWidth,
            shape,
            className,
          })
        )}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants }
